import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext();

// Token stored in memory only (not localStorage) for security
let inMemoryToken = null;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  // Get CSRF token from cookie
  const getCsrfToken = () => {
    const match = document.cookie.match(/csrf_token=([^;]+)/);
    return match ? match[1] : null;
  };

  const getAuthHeaders = useCallback(() => {
    const headers = {};
    if (inMemoryToken) headers.Authorization = `Bearer ${inMemoryToken}`;
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    return headers;
  }, []);

  // Schedule token refresh before expiry
  const scheduleRefresh = useCallback((expiresIn) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    // Refresh 60 seconds before expiry
    const refreshMs = Math.max((expiresIn - 60) * 1000, 30000);
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        inMemoryToken = res.data.token;
        setUser(res.data.user);
        scheduleRefresh(res.data.expires_in);
      } catch {
        // Refresh failed — session expired
        inMemoryToken = null;
        setUser(null);
      }
    }, refreshMs);
  }, []);

  // Axios interceptor: auto-refresh on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh') && !originalRequest.url?.includes('/auth/login') && !originalRequest.url?.includes('/auth/register')) {
          originalRequest._retry = true;
          try {
            const res = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
            inMemoryToken = res.data.token;
            setUser(res.data.user);
            scheduleRefresh(res.data.expires_in);
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${inMemoryToken}`;
            return axios(originalRequest);
          } catch {
            inMemoryToken = null;
            setUser(null);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [scheduleRefresh]);

  const checkAuth = useCallback(async () => {
    // Skip if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Try existing in-memory token
    if (inMemoryToken) {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${inMemoryToken}` },
          withCredentials: true
        });
        setUser(res.data);
        setLoading(false);
        return;
      } catch {
        inMemoryToken = null;
      }
    }

    // Try refresh token (httpOnly cookie)
    try {
      const res = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      inMemoryToken = res.data.token;
      setUser(res.data.user);
      scheduleRefresh(res.data.expires_in);
      setLoading(false);
      return;
    } catch {
      // No valid refresh token
    }

    // Try Google OAuth session cookie
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, [scheduleRefresh]);

  useEffect(() => {
    checkAuth();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const { token, user: userData, expires_in } = res.data;
    inMemoryToken = token;
    setUser(userData);
    scheduleRefresh(expires_in);
    return userData;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    const { token, user: userData, expires_in } = res.data;
    inMemoryToken = token;
    setUser(userData);
    scheduleRefresh(expires_in);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const setGoogleUser = (userData) => {
    setUser(userData);
    setLoading(false);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
    } catch {}
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    inMemoryToken = null;
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, setGoogleUser, logout, getAuthHeaders, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
